import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Menu, LogOut, Music, Calendar, HelpCircle, Users, Dumbbell, Scale, Library, TrendingUp, UserCheck, Sun, Moon, Monitor, Star, Target, Settings } from "lucide-react";
import { Link, useLocation } from "wouter";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { useThemeMode, type ThemeMode } from "@/hooks/useTheme";
import { usePendingRecommendations } from "@/hooks/usePendingRecommendations";
import { useAuth } from "@/hooks/useAuth";

interface MyCoachData {
  hasCoach: boolean;
}

export function MobileNav({ isAdmin = false, isCoach = false }: { isAdmin?: boolean; isCoach?: boolean }) {
  const [open, setOpen] = useState(false);
  const [location] = useLocation();
  const { themeMode, setThemeMode, mounted } = useThemeMode();
  const { pendingCount } = usePendingRecommendations();
  const { signOut } = useAuth();

  const { data: coachData } = useQuery<MyCoachData>({
    queryKey: ["/api/my-coach"],
    staleTime: 5 * 60 * 1000,
  });

  const hasCoach = coachData?.hasCoach || false;

  const cycleTheme = () => {
    if (themeMode === 'light') setThemeMode('dark');
    else if (themeMode === 'dark') setThemeMode('auto');
    else setThemeMode('light');
  };

  const getThemeIcon = () => {
    if (themeMode === 'light') return <Sun className="w-4 h-4 mr-2" />;
    if (themeMode === 'dark') return <Moon className="w-4 h-4 mr-2" />;
    return <Monitor className="w-4 h-4 mr-2" />;
  };

  const getThemeLabel = () => {
    if (themeMode === 'light') return 'Light Mode';
    if (themeMode === 'dark') return 'Dark Mode';
    return 'Auto';
  };

  const NavLink = ({ href, icon: Icon, label, emphasized = false }: { href: string; icon: any; label: string; emphasized?: boolean }) => (
    <Link href={href} onClick={() => setOpen(false)}>
      <Button 
        variant="ghost" 
        className={cn(
          "w-full justify-start hover:text-primary hover:bg-primary/10",
          location === href ? "text-primary bg-primary/10" : "text-foreground",
          emphasized && "font-medium"
        )}
      >
        <Icon className="w-4 h-4 mr-2" /> {label}
      </Button>
    </Link>
  );

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden text-foreground">
          <Menu className="w-6 h-6" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[300px] bg-card border-l border-border/50 p-6 pt-6 overflow-y-auto">
        <div className="flex flex-col gap-4 mt-6">
          
          {/* Primary Navigation */}
          <div className="flex flex-col gap-0.5">
            <NavLink href="/calendar" icon={Calendar} label="Calendar" />
            <NavLink href="/progress" icon={TrendingUp} label="My Progress" />
            <NavLink href="/music" icon={Music} label="My Beats" />
            {hasCoach && (
              <NavLink href="/my-coach" icon={UserCheck} label="My Coach" />
            )}
          </div>

          {/* Training Section */}
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground/60 px-3 mb-1">
              Training
            </span>
            <Link href="/my-programs" onClick={() => setOpen(false)}>
              <Button 
                variant="ghost" 
                className={cn(
                  "w-full justify-start hover:text-primary hover:bg-primary/10",
                  location === "/my-programs" ? "text-primary bg-primary/10" : "text-foreground"
                )}
              >
                <Library className="w-4 h-4 mr-2" />
                My Programs
                {pendingCount > 0 && (
                  <Star className="w-3 h-3 text-red-500 fill-red-500 ml-1" data-testid="mobile-indicator-pending-recommendations" />
                )}
              </Button>
            </Link>
            <NavLink href="/programs" icon={Dumbbell} label="Expert Programs" />
            <NavLink href="/training-blocks" icon={Target} label="My Training Blocks" />
            <NavLink href="/workout-preferences" icon={Scale} label="My Workout Preferences" />
          </div>

          {/* Admin/Coach Section */}
          {(isAdmin || isCoach) && (
            <div className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground/60 px-3 mb-1">
                {isAdmin ? "Admin" : "Coach"}
              </span>
              <Link href="/admin" onClick={() => setOpen(false)}>
                <Button variant="ghost" className="w-full justify-start text-primary hover:text-primary hover:bg-primary/10">
                  <Users className="w-4 h-4 mr-2" /> {isAdmin ? "Admin Dashboard" : "Coach Dashboard"}
                </Button>
              </Link>
            </div>
          )}

          {/* Support & Account */}
          <div className="mt-auto pt-4 border-t border-border/20 space-y-0.5">
            <div className="flex items-center justify-center w-full px-3 py-2">
              {mounted && (
                <div className="flex rounded-lg border border-border/50 p-0.5">
                  <button
                    onClick={() => setThemeMode('light')}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${themeMode === 'light' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                    data-testid="mobile-theme-light"
                  >
                    <Sun className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => setThemeMode('auto')}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${themeMode === 'auto' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                    data-testid="mobile-theme-auto"
                  >
                    <Monitor className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => setThemeMode('dark')}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${themeMode === 'dark' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                    data-testid="mobile-theme-dark"
                  >
                    <Moon className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
            <Link href="/preferences" onClick={() => setOpen(false)}>
              <Button variant="ghost" className="w-full justify-start text-muted-foreground hover:text-foreground hover:bg-primary/10 text-sm">
                <Settings className="w-4 h-4 mr-2" /> Account Settings
              </Button>
            </Link>
            <Link href="/tutorial" onClick={() => setOpen(false)}>
              <Button variant="ghost" className="w-full justify-start text-muted-foreground hover:text-foreground hover:bg-primary/10 text-sm">
                <HelpCircle className="w-4 h-4 mr-2" /> How To Use
              </Button>
            </Link>
            <Button 
              variant="ghost" 
              className="w-full justify-start text-muted-foreground hover:text-foreground"
              onClick={async () => {
                setOpen(false);
                await signOut();
              }}
              data-testid="button-logout"
            >
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
